import { forwardRef } from "react";
import { QRCode } from "antd";
import { formatMoney, formatQuantity } from "../../utils/quoteFormat";
import { useTranslation } from "../../i18n";
import "./QuotePoster.css";

const QR_SIZE = 104;

const QuotePoster = forwardRef(function QuotePoster({ quoteDisplay }, ref) {
    const { t, locale } = useTranslation();

    if (!quoteDisplay) {
        return null;
    }

    const {
        projectName,
        quoteDateLabel,
        validUntilLabel,
        currency,
        portfolioUrl,
        hideSubitemPrices,
        categories,
        grandTotal,
        globalNotes,
    } = quoteDisplay;

    const metaParts = [quoteDateLabel];
    if (validUntilLabel) {
        metaParts.push(`${t("quote.poster.validUntil")} ${validUntilLabel}`);
    }

    return (
        <div ref={ref} className="quote-poster">
            <div className="quote-poster__inner">
                <header className="quote-poster__header">
                    <div className="quote-poster__brand">HEI CREATIVE</div>
                    <div className="quote-poster__doc-title">
                        {t("quote.poster.title")}
                        <span className="quote-poster__doc-title-en">{t("quote.poster.titleEn")}</span>
                    </div>
                    <h1 className="quote-poster__project">
                        {projectName || t("quote.poster.projectPlaceholder")}
                    </h1>
                    {metaParts.filter(Boolean).length > 0 && (
                        <div className="quote-poster__meta">{metaParts.filter(Boolean).join(" · ")}</div>
                    )}
                </header>

                <section className="quote-poster__body">
                    {categories.map((category, categoryIndex) => (
                        <div key={category.key} className="quote-poster__section">
                            <div className="quote-poster__section-title">
                                <span className="quote-poster__section-index">
                                    {String(categoryIndex + 1).padStart(2, "0")}
                                </span>
                                <span>{t(`quote.categories.${category.key}`)}</span>
                            </div>

                            {category.items.map((item) => (
                                <div key={item.id} className="quote-poster__row">
                                    <div className="quote-poster__row-top">
                                        <span className="quote-poster__item-name">{item.name}</span>
                                        {!hideSubitemPrices && (
                                            <span className="quote-poster__item-amount">
                                                {formatMoney(item.subtotal, currency)}
                                            </span>
                                        )}
                                    </div>
                                    {!hideSubitemPrices && (
                                        <div className="quote-poster__item-calc">
                                            {formatMoney(item.unitPrice, currency)} × {formatQuantity(item.quantity)}
                                        </div>
                                    )}
                                    {item.note ? (
                                        <div className="quote-poster__item-note">{item.note}</div>
                                    ) : null}
                                </div>
                            ))}

                            {!hideSubitemPrices && (
                                <div className="quote-poster__section-subtotal">
                                    <span>{t("quote.poster.sectionSubtotal")}</span>
                                    <span>{formatMoney(category.subtotal, currency)}</span>
                                </div>
                            )}
                        </div>
                    ))}
                </section>

                <section className="quote-poster__total">
                    <span className="quote-poster__total-label">{t("quote.poster.grandTotal")}</span>
                    <span className="quote-poster__total-amount">{formatMoney(grandTotal, currency)}</span>
                </section>

                <footer className="quote-poster__footer">
                    {globalNotes.length > 0 && (
                        <div className="quote-poster__notes">
                            {globalNotes.map((note, index) => (
                                <div key={`poster-note-${index}`} className="quote-poster__note-line">
                                    {globalNotes.length > 1
                                        ? locale === "zh" || locale === "yue"
                                            ? `${index + 1}. `
                                            : "• "
                                        : ""}
                                    {note}
                                </div>
                            ))}
                        </div>
                    )}

                    <div className="quote-poster__qr-row">
                        <div className="quote-poster__qr-wrapper">
                            <div className="quote-poster__qr-inner">
                                {portfolioUrl ? (
                                    <QRCode
                                        value={portfolioUrl}
                                        size={QR_SIZE}
                                        color="#1d1d1f"
                                        bgColor="#ffffff"
                                        bordered={false}
                                        errorLevel="M"
                                    />
                                ) : null}
                            </div>
                        </div>
                        <div className="quote-poster__qr-text">
                            <div>{t("quote.poster.scanZh")}</div>
                            <div>{t("quote.poster.scanEn")}</div>
                        </div>
                    </div>

                    <div className="quote-poster__divider" />

                    <div className="quote-poster__social">
                        <div>{t("quote.poster.socialRed")}</div>
                        <div>{t("quote.poster.socialIg")}</div>
                    </div>
                    <div className="quote-poster__website">HEICREATIVE.CO.UK</div>
                </footer>
            </div>
        </div>
    );
});

export default QuotePoster;
